// src/pages/Dashboard.tsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, RefreshCw, Search, X } from 'lucide-react';
import { Email } from '../types';
import { CampaignSummary, ACTIVE_STAGES, TERMINAL_STAGES, StageCounts } from '../types/stage';
import { BulkAction, BulkSelector, dashboardService } from '../services/dashboard';
import QueueOverview from '../components/dashboard/QueueOverview';
import EmailTable from '../components/dashboard/EmailTable';
import EmailDetailsModal from '../components/dashboard/EmailDetailsModal';
import QueueControls from '../components/dashboard/QueueControls';
import StageChips from '../components/dashboard/StageChips';
import BulkActionBar from '../components/dashboard/BulkActionBar';
import CampaignTable from '../components/dashboard/CampaignTable';
import { apiService } from '../services/api';
import { QueueStats } from '../types';

type View = 'queue' | 'history' | 'campaigns';

// 100 rather than 50 so clearing out finished mail takes half as many passes.
// The API clamps pageSize to 500, so there is headroom to raise this further.
const PAGE_SIZE = 100;
const REFRESH_MS = 10000;

const VIEWS: { id: View; label: string; hint: string }[] = [
  { id: 'queue', label: 'Queue', hint: 'Live work: queued, sending and in transit' },
  { id: 'history', label: 'History', hint: 'Finished: delivered, bounced, failed, cancelled' },
  { id: 'campaigns', label: 'Campaigns', hint: 'Grouped by client reference prefix' },
];

const Dashboard: React.FC = () => {
  const [view, setView] = useState<View>('queue');

  const [emails, setEmails] = useState<Email[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);

  const [stageCounts, setStageCounts] = useState<StageCounts | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [stats, setStats] = useState<QueueStats | null>(null);

  const [stageFilter, setStageFilter] = useState<string | null>(null);
  const [heldOnly, setHeldOnly] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [allMatchingSelected, setAllMatchingSelected] = useState(false);
  const [pendingAction, setPendingAction] = useState<BulkAction | null>(null);

  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Keeps the polling effect from re-subscribing on every keystroke.
  const loadRef = useRef<() => void>(() => {});

  const describeError = (error: any, fallback: string) =>
    error?.response?.data?.error ?? error?.message ?? fallback;

  const selector: BulkSelector = useMemo(
    () => ({
      stage: stageFilter ?? undefined,
      priority: priorityFilter ? Number(priorityFilter) : undefined,
      clientReferencePrefix: campaignFilter || undefined,
      search: search || undefined,
    }),
    [stageFilter, priorityFilter, campaignFilter, search]
  );

  const load = useCallback(async () => {
    try {
      if (view === 'campaigns') {
        const { campaigns: rows } = await dashboardService.getCampaigns(50);
        setCampaigns(rows);
      } else {
        const data = await dashboardService.getQueue({
          page,
          pageSize: PAGE_SIZE,
          view,
          stage: stageFilter ?? undefined,
          held: heldOnly ? true : undefined,
          priority: priorityFilter ? Number(priorityFilter) : undefined,
          clientReferencePrefix: campaignFilter || undefined,
          search: search || undefined,
        });
        setEmails(data.emails);
        setTotalCount(data.totalCount);
        setTotalPages(data.totalPages);
      }

      const [counts, queueStats] = await Promise.all([
        dashboardService.getStageCounts(campaignFilter || undefined),
        apiService.get<QueueStats>('/dashboard/queue/status').catch(() => null),
      ]);
      setStageCounts(counts);
      if (queueStats) setStats(queueStats);

      setLastRefreshed(new Date());
    } catch (error) {
      setNotice({ type: 'error', text: describeError(error, 'Could not load the queue.') });
    } finally {
      setLoading(false);
    }
  }, [view, page, stageFilter, heldOnly, priorityFilter, campaignFilter, search]);

  loadRef.current = load;

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // The table used to refresh only when a filter changed, so a draining queue
  // looked frozen while it worked.
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => loadRef.current(), REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh]);

  // Debounce the free-text box so each keystroke is not a query.
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  const resetSelection = () => {
    setSelectedEmails([]);
    setAllMatchingSelected(false);
  };

  const changeView = (next: View) => {
    setView(next);
    setStageFilter(null);
    setHeldOnly(false);
    setPage(1);
    resetSelection();
  };

  const handleSelectEmail = (emailId: string) => {
    setAllMatchingSelected(false);
    setSelectedEmails((prev) =>
      prev.includes(emailId) ? prev.filter((id) => id !== emailId) : [...prev, emailId]
    );
  };

  const handleSelectAllOnPage = () => {
    if (allMatchingSelected) {
      resetSelection();
      return;
    }
    setSelectedEmails((prev) =>
      prev.length === emails.length ? [] : emails.map((e) => e.emailId)
    );
  };

  const runBulk = async (action: BulkAction) => {
    setBusy(true);
    try {
      const result = allMatchingSelected
        ? await dashboardService.bulkBySelector(action, selector, totalCount)
        : await dashboardService.bulkByIds(action, selectedEmails);

      // Nothing matching is not a success. Saying "applied to 0 emails" in the
      // green banner reads as though it worked, which is how a silently refused
      // action looks like a broken button.
      setNotice(
        result.matched === 0
          ? {
              type: 'error',
              text: `Nothing was ${action}d — none of the selected emails were in a state that allows it.`,
            }
          : {
              type: 'success',
              text:
                result.affected === result.matched
                  ? `${action} applied to ${result.affected.toLocaleString()} ${result.affected === 1 ? 'email' : 'emails'}.`
                  : `${action} applied to ${result.affected.toLocaleString()} of ${result.matched.toLocaleString()} matched — the rest were not in a state that allows it.`,
            }
      );
      resetSelection();
      await load();
    } catch (error: any) {
      if (error?.response?.status === 409) {
        setNotice({
          type: 'error',
          text: describeError(error, 'The set changed while you were confirming. Nothing was altered.'),
        });
        await load();
      } else {
        setNotice({ type: 'error', text: describeError(error, `Could not ${action} those emails.`) });
      }
    } finally {
      setBusy(false);
      setPendingAction(null);
    }
  };

  const handleAction = (action: BulkAction) => {
    // Anything irreversible, or anything sweeping the whole filter rather than a
    // hand-picked page, gets confirmed with the number attached.
    const destructive = action === 'delete' || action === 'cancel';
    if (allMatchingSelected || destructive) {
      setPendingAction(action);
      return;
    }
    runBulk(action);
  };

  const handleRetryEmail = async (emailId: string) => {
    try {
      await dashboardService.retryEmail(emailId);
      setNotice({ type: 'success', text: 'Email put back in the queue.' });
      await load();
    } catch (error) {
      setNotice({ type: 'error', text: describeError(error, 'Could not requeue that email.') });
    }
  };

  const handleDeleteEmail = async (emailId: string) => {
    try {
      await dashboardService.deleteEmail(emailId);
      setNotice({ type: 'success', text: 'Email deleted.' });
      await load();
    } catch (error) {
      setNotice({ type: 'error', text: describeError(error, 'Could not delete that email.') });
    }
  };

  const clearFilters = () => {
    setStageFilter(null);
    setHeldOnly(false);
    setPriorityFilter('');
    setCampaignFilter('');
    setSearchInput('');
    setSearch('');
    setPage(1);
    resetSelection();
  };

  const hasFilters =
    !!stageFilter || heldOnly || !!priorityFilter || !!campaignFilter || !!search;

  const targetCount = allMatchingSelected ? totalCount : selectedEmails.length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Email Queue</h1>
            <p className="mt-1 text-sm text-gray-600">
              Monitor and manage delivery across your sending runs
            </p>
          </div>

          <div className="flex items-center gap-3 text-sm">
            <label className="flex items-center gap-2 text-gray-600">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              Auto-refresh
            </label>
            <button
              onClick={() => load()}
              className="flex items-center gap-2 px-3 py-2 font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <RefreshCw size={15} />
              Refresh
            </button>
            {lastRefreshed && (
              <span className="text-xs text-gray-500 tabular-nums">
                {lastRefreshed.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>

        <QueueControls onStatusChange={load} />
        <QueueOverview stats={stats} />

        {notice && (
          <div
            role="status"
            className={`mb-4 px-4 py-3 rounded-lg border text-sm flex items-start justify-between gap-4 ${
              notice.type === 'success'
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            <span>{notice.text}</span>
            <button
              onClick={() => setNotice(null)}
              aria-label="Dismiss"
              className="opacity-70 hover:opacity-100"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* View tabs. Priority ordering is right for a send queue and wrong for a
            history, so the two are genuinely different views, not one filter. */}
        <div className="border-b border-gray-200 mb-4">
          <nav className="flex gap-6">
            {VIEWS.map((v) => (
              <button
                key={v.id}
                onClick={() => changeView(v.id)}
                title={v.hint}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  view === v.id
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {v.label}
              </button>
            ))}
          </nav>
        </div>

        {view !== 'campaigns' && (
          <>
            <StageChips
              counts={stageCounts}
              selected={stageFilter}
              onSelect={(s) => {
                setStageFilter(s);
                setHeldOnly(false);
                setPage(1);
                resetSelection();
              }}
              stages={view === 'queue' ? ACTIVE_STAGES : TERMINAL_STAGES}
              heldSelected={heldOnly}
              onToggleHeld={() => {
                setHeldOnly((h) => !h);
                setStageFilter(null);
                setPage(1);
                resetSelection();
              }}
            />

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[220px]">
                  <Search
                    size={15}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search recipient or subject"
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <input
                  value={campaignFilter}
                  onChange={(e) => {
                    setCampaignFilter(e.target.value);
                    setPage(1);
                    resetSelection();
                  }}
                  placeholder="Campaign reference prefix"
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 min-w-[200px]"
                />

                <select
                  value={priorityFilter}
                  onChange={(e) => {
                    setPriorityFilter(e.target.value);
                    setPage(1);
                    resetSelection();
                  }}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All priorities</option>
                  <option value="1">Critical</option>
                  <option value="2">High</option>
                  <option value="3">Normal</option>
                  <option value="4">Low</option>
                  <option value="5">Bulk</option>
                </select>

                {hasFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-gray-600 hover:text-gray-900 underline underline-offset-2"
                  >
                    Clear filters
                  </button>
                )}

                <span className="ml-auto text-sm text-gray-500 tabular-nums">
                  {totalCount.toLocaleString()} {totalCount === 1 ? 'email' : 'emails'}
                </span>
              </div>
            </div>

            <BulkActionBar
              selectedCount={selectedEmails.length}
              matchingCount={totalCount}
              allMatchingSelected={allMatchingSelected}
              onSelectAllMatching={() => setAllMatchingSelected(true)}
              onClearSelection={resetSelection}
              onAction={handleAction}
              busy={busy}
            />
          </>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {view === 'campaigns' ? (
            <CampaignTable
              campaigns={campaigns}
              isLoading={loading}
              onDrillIn={(campaign) => {
                setCampaignFilter(campaign);
                setView('queue');
                setStageFilter(null);
                setPage(1);
                resetSelection();
              }}
            />
          ) : (
            <EmailTable
              emails={emails}
              selectedEmails={selectedEmails}
              allMatchingSelected={allMatchingSelected}
              onSelectEmail={handleSelectEmail}
              onSelectAll={handleSelectAllOnPage}
              onDeleteEmail={handleDeleteEmail}
              onRetryEmail={handleRetryEmail}
              onViewDetails={setSelectedEmail}
              isLoading={loading}
              emptyHint={
                view === 'queue'
                  ? 'Nothing is queued or in flight right now. Finished mail is under History.'
                  : 'No finished emails match this filter.'
              }
            />
          )}
        </div>

        {view !== 'campaigns' && totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between text-sm">
            <button
              onClick={() => {
                setPage((p) => Math.max(1, p - 1));
                resetSelection();
              }}
              disabled={page === 1}
              className="px-3 py-2 font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-gray-700 tabular-nums">
              Page {page.toLocaleString()} of {totalPages.toLocaleString()}
            </span>
            <button
              onClick={() => {
                setPage((p) => Math.min(totalPages, p + 1));
                resetSelection();
              }}
              disabled={page === totalPages}
              className="px-3 py-2 font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}

        <EmailDetailsModal
          email={selectedEmail}
          isOpen={!!selectedEmail}
          onClose={() => setSelectedEmail(null)}
          onRetry={handleRetryEmail}
          onCancel={handleDeleteEmail}
        />

        {/* Confirmation. The number is the point: acting on a filter is how you
            cancel the wrong campaign, so it is stated before you commit. */}
        {pendingAction && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="text-amber-600" size={22} />
                <h3 className="text-lg font-semibold capitalize">{pendingAction} emails</h3>
              </div>
              <p className="text-gray-700 mb-2">
                This will {pendingAction}{' '}
                <strong className="tabular-nums">{targetCount.toLocaleString()}</strong>{' '}
                {targetCount === 1 ? 'email' : 'emails'}
                {allMatchingSelected ? ' matching the current filter' : ' you selected'}.
              </p>
              {pendingAction === 'delete' && (
                <p className="text-sm text-red-700 mb-2">
                  Delete removes the record entirely. Cancel keeps it, marked as withdrawn.
                </p>
              )}
              <p className="text-gray-700 mb-6 text-sm">
                If the number has changed by the time this runs, nothing will be altered.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setPendingAction(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={() => runBulk(pendingAction)}
                  disabled={busy}
                  className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                    pendingAction === 'delete'
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {busy ? 'Working…' : `Yes, ${pendingAction} ${targetCount.toLocaleString()}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
